import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { uploadFile, getImageUrl } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUploader({ value, onChange, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bilddateien erlaubt");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Maximale Dateigröße: 10MB");
      return;
    }
    setUploading(true);
    try {
      const result = await uploadFile(file);
      onChange(result.path);
      toast.success("Bild hochgeladen");
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleUpload(file);
  };

  const imageUrl = value ? getImageUrl(value) : null;

  return (
    <div className={className}>
      {imageUrl ? (
        <div className="relative group rounded-2xl overflow-hidden" data-testid="image-preview">
          <img
            src={imageUrl}
            alt="Upload"
            className="w-full h-48 object-cover"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            data-testid="remove-image-btn"
          >
            <X className="w-4 h-4 text-slate-700" />
          </button>
        </div>
      ) : (
        <div
          className={`upload-zone rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer ${dragOver ? 'drag-over' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="image-upload-zone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-slate-500 font-medium">Wird hochgeladen...</span>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-3">
                <ImageIcon className="w-6 h-6 text-indigo-500" />
              </div>
              <span className="text-sm font-medium text-slate-600">Bild hochladen</span>
              <span className="text-xs text-slate-400 mt-1">oder per Drag & Drop</span>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleUpload(e.target.files[0])}
        data-testid="image-file-input"
      />
    </div>
  );
}
